// @flow
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/client/react/hooks";
import { CalendlyWidget } from "@nested/brand";
import { RM_QUERY } from "./SignPost";
import { Heading, QuestionWrapper } from "../components";
import { type QuestionProps } from "../components/MultiStageForm";

const getCalendlySettings = (reasonForContact, rm) => {
  if (
    reasonForContact === "HOME_VISIT" ||
    reasonForContact === "SCHEDULE_A_VALUATION"
  ) {
    return {
      title: "When shall we meet?",
      description:
        "Let us know a convenient time for your local agent to come to your home for a valuation. We'll also talk you through selling with Nested, and answer any questions you might have.",
      calendlyUrl: rm.homeVisitBookingUrl,
    };
  }

  return {
    title: "When can we call?",
    description:
      "Let us know a convenient time to chat. We'll talk you through selling with Nested, and answer any questions you might have.",
    calendlyUrl: rm.introCallBookingUrl,
  };
};

/*
 * N.B This component can only be used in one place. It is paired with the CalendlyCallback
 * component (they must always be used together) and is currently in use in the FinishingUpForm
 * as part of the main onboarding flow so it cannot be reused in any other MultiStageForm.
 *
 * The '-2' suffix on the RM's personal url (see below) links to a specific calendly url
 * which, on booking a meeting, redirects the browser to `/get-started/callback` with the info
 * for the event in the query params. It's because of this hard wiring that the component is tied to
 * that one form.
 *
 * If you are desperate, you could make a third calendly page for every single RM with a callback that
 * leads to another form, but that would be insane. Alternatively, to use Calendly in a form, use the
 * 'Calendly' question instead. The downside of that component is you can't collect any details about
 * the scheduled event (e.g. date/time) like you can with CalendlyOnboarding+CalendlyCallback.
 */
export const CalendlyOnboarding = ({
  BackButton,
  next,
  updateFormState,
}: QuestionProps<"calendlyOnboarding">) => {
  const formState = useSelector((state) => state.onboardingForm);
  const { data, loading } = useQuery(RM_QUERY, {
    variables: { postcode: formState.address.postcode },
  });

  if (loading) {
    return null;
  }

  const rm = data?.postcodeDetails?.relationshipManager;

  const { calendlyUrl, title } = getCalendlySettings(
    formState.reasonForContact,
    rm,
  );

  return (
    <QuestionWrapper>
      <BackButton />
      <Heading>{title}</Heading>

      <CalendlyWidget
        email={
          formState.contactDetails
            ? formState.contactDetails?.email
            : formState.email?.email
        }
        name={
          formState.contactDetails?.name ||
          `${formState.name.firstName} ${formState.name.lastName}`
        }
        phone={formState.phone}
        address={`${formState.address.label}, ${formState.address.postcode}`}
        // url="https://calendly.com/d/g8vh-bzx8/in-person-valuation-with-test-nested" // Use this for testing locally
        url={`${calendlyUrl}-2`}
        onSuccess={() => updateFormState({ calendlyOnboarding: true })}
        onFailure={() => next(false)}
        onSkip={() => next(false)}
      />
    </QuestionWrapper>
  );
};
